import React, { FC, useState } from 'react';
import { Field, FieldProps } from 'formik';
import { SelectInputType, STANDARD_INPUT_HEIGHT } from '../../..';
import Error from '../../../error';
import { Label } from '../../../label';
import { useSelect } from '../../base';
import { OptionProps } from '../dropdown-select/RenderingStyles';
import SearchLineIcon from 'remixicon-react/SearchLineIcon';
import { useFuzzySearch } from '../../../../useFuzzySearch';

// active.iconPresent
const bgColorMap = {
  true: {
    true: 'white',
    false: 'primary',
  },
  false: {
    true: '',
    false: '',
  },
};

const borderColorMap = {
  true: {
    true: 'primary',
    false: 'primary',
  },
  false: {
    true: 'grey-100',
    false: 'grey-100',
  },
};

// active.iconPresent
const textColorMap = {
  true: {
    true: 'primary',
    false: 'white',
  },
  false: {
    true: 'grey-800',
    false: 'grey-800',
  },
};

export type BoxSelectInputType = SelectInputType & {
  perRow?: number;
  showBoxSelectSearchBox?: boolean;
  boxSelectSearchBoxPlaceholder?: string;
  showMaxRenderLimit?: number;
  customBoxStyle?: boolean;
  searchBoxInitialValue?: string;
};
export const BoxSelect = (props: BoxSelectInputType) => {
  const CustomField: FC<FieldProps> = () => {
    const selectTools = useSelect({
      name: props.name,
      multiSelect: props.multiSelect,
    });

    const SelectChip = (chipProps: OptionProps) => {
      const imagePresent = chipProps.image ? 'true' : 'false';
      const active = selectTools.isSelected(chipProps.value) ? 'true' : 'false';
      const borderWidth = imagePresent ? 'border-2' : 'border-1.5';
      let bgColor = bgColorMap[active][imagePresent];
      let borderColor = borderColorMap[active][imagePresent];
      let textColor = textColorMap[active][imagePresent];
      const fontWeight = active ? 'font-medium' : 'font-normal';
      const Icon = chipProps.icon;

      if (props.customBoxStyle && active === 'true') {
        bgColor = 'blue-100';
        borderColor = 'blue-300';
        textColor = 'grey-800';
      }

      return (
        <div
          onClick={() => {
            selectTools.toggleSelection(chipProps.value);
            selectTools.persistToFormik();
            // formikFieldProps.form.setFieldTouched(props.name, true);
          }}
          style={{
            height: `${imagePresent !== 'true' ? STANDARD_INPUT_HEIGHT : undefined}px`,
            backgroundColor: `var(--color-${bgColor})`,
            borderColor: `var(--color-${borderColor})`,
            color: `var(--color-${textColor})`,
          }}
          className={`flex cursor-pointer items-center justify-center ${
            active === 'false' && 'hover:border-grey-200 hover:bg-blue-100'
          } flex-1 ${Icon ? 'flex-row gap-2' : 'flex-col'} rounded-lg px-4 py-2 ${borderWidth} ${fontWeight}`}
        >
          {Icon && <Icon size={22} xHeight={20} />}
          {imagePresent === 'true' && (
            <img
              src={chipProps.image}
              alt={chipProps.value as string}
              className="mb-1"
              style={{
                maxHeight: '58px',
              }}
            />
          )}
          <div className="whitespace-nowrap">{chipProps.mainText || chipProps.value}</div>
        </div>
      );
    };

    const options = props.options ? props.options[0].options : [];

    const [searchTerm, setSearchTerm] = useState<string>(props.searchBoxInitialValue ?? '');

    const filteredOptions = useFuzzySearch([...options] as OptionProps[], searchTerm, (option) => ({
      mainText: option?.mainText,
    }));

    const limitedOptions = props.showMaxRenderLimit
      ? filteredOptions.slice(0, props.showMaxRenderLimit)
      : filteredOptions;

    return (
      <div className="w-full">
        <Label name={props.name} label={props.label} />
        {props.showBoxSelectSearchBox && (
          <div className="rounded-large border-1.5 border-grey-300 mt-2 mb-2 flex items-center gap-x-2 p-2">
            <SearchLineIcon className="text-grey-500" size={20} />
            <input
              type="text"
              placeholder={props.boxSelectSearchBoxPlaceholder || 'Search'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-transparent focus:outline-none"
            />
          </div>
        )}
        <div
          style={{
            gridTemplateColumns: props.perRow ? `repeat(${props.perRow}, minmax(0, 1fr))` : 'auto',
          }}
          className={`${props.perRow === undefined ? 'flex flex-wrap' : `grid`} ${
            props.disabled ? `pointer-events-none cursor-not-allowed opacity-50` : `cursor-auto`
          }

           gap-x-2 gap-y-1.5 `}
        >
          {limitedOptions.map((option, idx) => {
            return <SelectChip key={idx} {...option} />;
          })}
        </div>
        <Error name={props.name} />
      </div>
    );
  };

  return <Field component={CustomField} name={props.name} />;
};
